import { getCompanyTeamsRequest } from '@vyce/core/src/api/teams';
import { getSitesRequest } from '@vyce/core/src/api/time';
import { getPaySchedulesForInvitesRequest } from '@vyce/core/src/api/pay';
import { CompanyRoleDTO, CompanyRolesResponseDTO } from '@vyce/core/src/api/types';
import { MODULES } from '@vyce/core/src/types';

import { getResourceKeysByKind, getResourceKind, StaffResourceKind } from './resourceKeys';

// Shared staff-permission helpers used by the Edit Permissions dialog
// (views/staff-management) and the per-user Roles & Permissions accordion
// (modules/rolesModule). The permission model is module → role + resources.

// Re-exported so existing importers keep working; the aliasing itself lives in
// resourceKeys.ts, which is api-free.
export { getResourceKeysByKind, getResourceKind };
export type { StaffResourceKind };

// Module display names travel in several spellings ('Time&Attendance' in the
// legacy staff payload, 'Time & Attendance' in the IAM one). Compare through
// this rather than by literal key.
export const normaliseModuleName = (name?: string) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export interface StaffResourceOption {
  id: string;
  name: string;
}

export interface StaffModuleResources {
  teams: StaffResourceOption[];
  sites: StaffResourceOption[];
  paySchedules: StaffResourceOption[];
}

export const requiresResourceSelection = (role: CompanyRoleDTO | null) =>
  getResourceKind(role?.resource_type) !== 'company';

// Roles that are not scoped to a concrete resource are saved against the company itself.
export const getResourceIdsByRole = (
  role: CompanyRoleDTO | null,
  selectedIds: string[],
  companyId: string | null
) => {
  if (!companyId) return [];
  if (!requiresResourceSelection(role)) {
    return [companyId];
  }
  return selectedIds;
};

// The backend keys the resources map inconsistently across modules, so match
// against every known alias of the role's resource type.
export const getStoredResourceIds = (
  role: CompanyRoleDTO | null,
  resourcesMap: Record<string, string[]>
) => {
  const resourceKind = getResourceKind(role?.resource_type);
  const resourceType = role?.resource_type?.toLowerCase();
  const normalizedResourcesMap = Object.entries(resourcesMap || {}).reduce<Record<string, string[]>>(
    (acc, [key, value]) => {
      acc[key.toLowerCase()] = value;
      return acc;
    },
    {}
  );
  const candidateKeys = Array.from(
    new Set([resourceType, ...getResourceKeysByKind(resourceKind)].filter(Boolean) as string[])
  );

  let existingIds: string[] = [];
  candidateKeys.some(key => {
    const ids = normalizedResourcesMap[key];
    if (ids?.length) {
      existingIds = ids;
      return true;
    }
    return false;
  });

  return existingIds;
};

export const getExistingResourceIds = (
  role: CompanyRoleDTO | null,
  availableIds: string[],
  resourcesMap: Record<string, string[]>
) => {
  const existingIds = getStoredResourceIds(role, resourcesMap);
  if (!existingIds.length) return [];
  const availableSet = new Set(availableIds);
  return existingIds.filter(id => availableSet.has(id));
};

export const toggleId = (ids: string[], id: string, checked: boolean) =>
  checked ? Array.from(new Set([...ids, id])) : ids.filter(item => item !== id);

/**
 * Which resource inventories the caller is allowed to read. A kind left out is
 * not fetched at all and comes back empty — the point is to not fire a request
 * that is guaranteed to 403, not to hide its result afterwards.
 * Everything defaults to true so existing callers keep their behaviour.
 */
export interface StaffModuleResourceAccess {
  sites?: boolean;
  teams?: boolean;
  paySchedules?: boolean;
}

/** The loader a host app can swap: legend-app reads the same inventories over /admin/v1. */
export type FetchStaffModuleResources = (
  companyId: string,
  access?: StaffModuleResourceAccess
) => Promise<StaffModuleResources>;

// One inventory failing must not blank the other two — and must not error the whole
// permissions panel, which is what a rejected Promise.all did.
const orNull = async <T>(request: () => Promise<T>): Promise<T | null> => {
  try {
    return await request();
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const fetchStaffModuleResources: FetchStaffModuleResources = async (
  companyId: string,
  access: StaffModuleResourceAccess = {}
): Promise<StaffModuleResources> => {
  const { sites = true, teams = true, paySchedules = true } = access;

  const [teamsRes, sitesRes, paySchedulesRes] = await Promise.all([
    teams
      ? orNull(() =>
          getCompanyTeamsRequest({
            companyId,
            requestData: { limit: 1000, offset: 0, order_by: [{ field_name: 'name', desc: false }] },
          })
        )
      : null,
    sites
      ? orNull(() =>
          getSitesRequest(companyId, {
            limit: 1000,
            offset: 0,
            order_by: [{ field_name: 'name', desc: false }],
            show_deleted: false,
            only_active: true,
          })
        )
      : null,
    paySchedules ? orNull(() => getPaySchedulesForInvitesRequest({ token: '', companyId })) : null,
  ]);
  const scheduleItems = paySchedulesRes?.data?.items || paySchedulesRes?.data || [];
  return {
    teams: (teamsRes?.data?.items || []).map((item: any) => ({ id: item.uuid, name: item.name })),
    sites: (sitesRes?.data?.items || []).map((item: any) => ({ id: item.uuid, name: item.name })),
    paySchedules: ((scheduleItems || []) as any[]).map((item: any) => ({
      id: item.uuid,
      name: item.name,
    })),
  };
};

export interface StaffModuleMeta {
  key: MODULES;
  label: string;
  rolesKey: string;
  resourceKind: Exclude<StaffResourceKind, 'company'> | null;
  resourceLabel?: string;
  /** Canonical IAM key ('time', 'teams', 'pay', …) — the key for anything new. */
  systemName?: string;
}

// The five permission modules of the Edit Permissions flow, in display order.
export const STAFF_MODULES: StaffModuleMeta[] = [
  {
    key: MODULES.COMPANY,
    label: 'Company',
    rolesKey: 'Company',
    resourceKind: null,
    systemName: 'companies',
  },
  {
    key: MODULES.PAYROLL,
    label: 'Payroll',
    rolesKey: 'Payroll',
    resourceKind: 'paySchedules',
    resourceLabel: 'Which Pay Schedule?',
    systemName: 'pay',
  },
  {
    key: MODULES.CONNECT,
    label: 'Hiring',
    rolesKey: 'Connect',
    resourceKind: null,
    systemName: 'connect',
  },
  {
    key: MODULES.TIME,
    label: 'Time & Attendance',
    rolesKey: 'Time&Attendance',
    resourceKind: 'sites',
    resourceLabel: 'Which Location?',
    systemName: 'time',
  },
  {
    key: MODULES.TEAMS,
    label: 'Teams',
    rolesKey: 'Teams',
    resourceKind: 'teams',
    resourceLabel: 'Which Teams?',
    systemName: 'teams',
  },
];

// Fallback for a module that has no resource-scoped role assigned yet, so its
// resource kind can't be read off role.resource_type.
const MODULE_RESOURCE_KINDS: Record<string, Exclude<StaffResourceKind, 'company'>> = {
  time: 'sites',
  teams: 'teams',
  pay: 'paySchedules',
};

export const getModuleResourceKind = (
  systemName: string,
  roles: CompanyRoleDTO[] = []
): Exclude<StaffResourceKind, 'company'> | null => {
  const scopedRole = roles.find(role => requiresResourceSelection(role));
  if (scopedRole) {
    const kind = getResourceKind(scopedRole.resource_type);
    return kind === 'company' ? null : kind;
  }
  return MODULE_RESOURCE_KINDS[systemName] || null;
};

export const RESOURCE_KIND_LABELS: Record<Exclude<StaffResourceKind, 'company'>, string> = {
  sites: 'Locations',
  teams: 'Teams',
  paySchedules: 'Pay schedules',
};

// roles/available is keyed by module display name, which the two backends spell
// differently — match through normaliseModuleName.
export const pickModuleRoles = (
  rolesData: CompanyRolesResponseDTO | undefined,
  moduleDisplayName: string
): CompanyRoleDTO[] => {
  if (!rolesData) return [];
  const direct = rolesData[moduleDisplayName];
  if (direct) return direct;
  const target = normaliseModuleName(moduleDisplayName);
  const match = Object.keys(rolesData).find(key => normaliseModuleName(key) === target);
  return match ? rolesData[match] : [];
};

// staffItem.modules is keyed by module display name too.
export const pickStaffModuleRoleName = (
  modulesMap: Record<string, string> | undefined,
  moduleDisplayName: string
): string | undefined => {
  if (!modulesMap) return undefined;
  const direct = modulesMap[moduleDisplayName];
  if (direct) return direct;
  const target = normaliseModuleName(moduleDisplayName);
  const match = Object.keys(modulesMap).find(key => normaliseModuleName(key) === target);
  return match ? modulesMap[match] : undefined;
};
